// @flow
import {action, observable, runInAction, makeObservable} from 'mobx';
import type {IObservableArray} from 'mobx';
import RootStore from 'mobx/RootStore';
import {SessionDocumentV1} from './model';
import {Models} from '@wellstone-solutions/common';
import type {ApiResponseType} from '@wellstone-solutions/common';
import type {
  UISessionDocumentType,
  UISessionServiceType,
  SessionDocumentStatus,
  ApiPDFResponseType,
} from 'modules/documentation/model/types';

const {Integration} = Models;

// Legacy V1 REST data layer (org-in-path, snake_case, nested member/staff),
// served behind the documentation_v2 flag during rollout. The V2 path lives in
// the api-client-backed React Query hooks (see queries/ and
// hooks/useSessionDocumentActions); remove this store when the flag is retired.
export class SessionDocumentStore {
  rootStore: RootStore;
  serviceTypes: IObservableArray<UISessionServiceType> = observable.array();

  constructor(rootStore: RootStore) {
    makeObservable(this, {getServiceTypes: action});
    this.rootStore = rootStore;
  }

  // $FlowIgnoreMe - meStore is injected on rootStore.stores
  get _orgId(): string {
    return this.rootStore.stores.meStore.organizationId;
  }

  async createSessionDoc(
    sessionDoc: UISessionDocumentType,
  ): Promise<ApiResponseType<UISessionDocumentType>> {
    const payload = SessionDocumentV1.toInputApi(sessionDoc);
    const response = await SessionDocumentV1.create(this._orgId, payload);
    return this._transformResponse(response);
  }

  async updateSessionDoc(
    id: string,
    sessionDoc: UISessionDocumentType,
  ): Promise<ApiResponseType<UISessionDocumentType>> {
    const payload = SessionDocumentV1.toInputApi(sessionDoc);
    const response = await SessionDocumentV1.update(this._orgId, id, payload);
    return this._transformResponse(response);
  }

  async updateSessionDocStatus(
    id: string,
    sessionDoc: UISessionDocumentType,
    status: SessionDocumentStatus,
  ): Promise<ApiResponseType<UISessionDocumentType>> {
    const payload = SessionDocumentV1.toInputApi({...sessionDoc, status});
    const response = await SessionDocumentV1.updateFields(
      this._orgId,
      id,
      payload,
    );
    return this._transformResponse(response);
  }

  async getSessionDoc(
    id: string,
  ): Promise<ApiResponseType<UISessionDocumentType>> {
    const response = await SessionDocumentV1.get(this._orgId, id);
    return this._transformResponse(response);
  }

  async createExternalEHRDocument(
    id: string,
  ): Promise<ApiResponseType<UISessionDocumentType>> {
    const response = await Integration.create(this._orgId, id);

    if (!response.isSuccess) {
      // $FlowIgnoreMe
      return response;
    }

    return {...response, data: SessionDocumentV1.toUI(response.data)};
  }

  async updateExternalEHRDocument(
    id: string,
  ): Promise<ApiResponseType<UISessionDocumentType>> {
    const response = await Integration.update(this._orgId, id);

    if (!response.isSuccess) {
      // $FlowIgnoreMe
      return response;
    }

    return {...response, data: SessionDocumentV1.toUI(response.data)};
  }

  _transformResponse(
    response: ApiResponseType<Object>,
  ): ApiResponseType<UISessionDocumentType> {
    if (!response.isSuccess) {
      // $FlowIgnoreMe
      return response;
    }

    // V1 responses embed member/staff, so a synchronous transform is enough.
    return {...response, data: SessionDocumentV1.toUI(response.data)};
  }

  async downloadPDF(id: string): Promise<ApiResponseType<ApiPDFResponseType>> {
    const response = await SessionDocumentV1.getPDF(this._orgId, id);

    if (response.isSuccess) {
      // response contains Base64 encoded string for the file
      const url = window.URL.createObjectURL(
        new Blob([atob(response.data.file)], {type: 'application/pdf'}),
      );

      // dynamically add link, define attrs and click it
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body?.appendChild(link);
      link.click();

      // remove the link
      link.parentNode?.removeChild(link);
    }

    return response;
  }

  async getServiceTypes(): Promise<
    ApiResponseType<Array<UISessionServiceType>>,
  > {
    if (this.serviceTypes.length > 0) {
      // $FlowIgnoreMe
      return Promise.resolve({
        isSuccess: true,
        data: Array.from(this.serviceTypes),
      });
    }

    const response = await SessionDocumentV1.getServiceTypes(this._orgId);

    if (!response.isSuccess) {
      // $FlowIgnoreMe
      return response;
    }

    const uiServiceTypes = SessionDocumentV1.serviceTypesToUIList(
      response.data,
    );

    runInAction(() => {
      this.serviceTypes.replace(uiServiceTypes);
    });

    // format for UI
    return {
      ...response,
      data: uiServiceTypes,
    };
  }

  async removeSessionDoc(id: string): Promise<ApiResponseType<{}>> {
    return SessionDocumentV1.remove(this._orgId, id);
  }
}
